import createGraphqlClient from "@/utils/apollo";

export const fragments = {
	Document: `
		fragment Document on DocumentType {
			id
			path
			file_name
		}
	`,
	File: `
		fragment File on File {
			id
			path
			file_name
			extension
		}
	`,
	BackendUser: `
		fragment BackendUser on BackendUser {
			id
			email
			avatar {
				...Document
			}
			first_name
			last_name
		}
	`,
	Note: `
		fragment Note on Note {
			id
			color
			title
			excerpt
			content
			sort_order
			created_at
    		updated_at
			user {
				...BackendUser
			}
			files {
				...File
			}
		}
	`,
	NotePagination: `
		fragment NotePagination on CbsNotePagination {
			total
			has_more_pages
			data {
				...Note
			}
		}
	`,
	CommentPagination: `
		fragment CommentPagination on CommentPagination {
			total
			has_more_pages
			data {
				id
				title
				parent_id
				created_at
				updated_at
				author {
					id
					first_name
					last_name
					avatar {
						path
					}
				}
				attachs {
					id
					path
					file_name
					extension
				}
			}
		}
	`,
	TodoItemWithOutCommentsPagination: `
		fragment TodoItemWithOutCommentsPagination on Todolist {
			id
			name
			type
			code
			can_complete
			can_cancel
			description
			properties
			employees {
				id
				name
				avatar(w: 100, h: 100)
				department {name}
			}
			relations{
				id
				type
				display
			}
			speech {
				path
			}
			assignees{type id name}
			creater {
				first_name
				last_name
				employee {
					department {
						name
						color
					}
				}
			}
			updated_at
		}

	`,
	TodoItemBasicPagination: `
		fragment TodoItemBasicPagination on Todolist {
			id
			code
			name
			type
			can_update
			can_cancel
			can_complete
			can_add_detail
			description
			properties
			is_over_due
			project_id
			parent {
				id
				name
			}
			user_option {
				color
			}
			assignees {
				id
				type
				name
			}
			relations {
				id
				display
				type
			}
			creater {
				first_name
				last_name
				employee {
					department {
						name
						color
					}
				}
			}
			updated_at
			done_at
			status_object {
				key
				value
			}

			employees {
				id
				avatar (w:160,h:160)
				name
			}
			changable_statuses{
				key
				value
			}
			forms {
				id
			}
			from_type_id

		}
	`,
	TodoItemDetail: `
		fragment TodoItemDetail on Todolist {
			id
			name
			type
			code
			project_id
			forms {
				id
				name
				code
				properties
				display_name
				attachs {
					id
					path
					file_name
					file_size
					extension
				}
				controls {
					id
					title
					control
					children
					attributes
					is_optional
					description {
						value
						enable
						position
					}
				}
				is_submited
				can_update
				can_delete
			}
			can_update
			can_complete
			can_cancel
			description
			can_add_detail
			properties
			can_attach_files
			relations{
				id
				type
				display
			}
			speech {
				path
			}
			assignees{type id name}
			creater {
				first_name
				last_name
				employee {
					display_name
					department {
						name
						color
					}
				}
			}
			todoitems (limit: -1, page: 1) {
				data { id title done_at }
			}
			children {
				id
				name
				can_update
				can_cancel
				can_add_detail
				properties
				is_over_due
				watchers {
					relations{type id name}
					content
					hashtag_content
					can_edittable
					remind_unit
					remind_value
					reminded_at
				}
				assignees {
					id
					type
					name
				}
				employees {
					name
					avatar (w:25,h:25)
				}
				changable_statuses{
					key
					value
				}
				forms {
					id
					name
				}
				status_object {
					key
					value
				}
			}
			watchers {
				relations{type id name}
				content
				hashtag_content
				can_edittable
				remind_unit
				remind_value
				reminded_at
			}
			attachs{
				id
				path
				file_name
				file_size
				extension
			}
			reminders {
				id
			   	period_type
				milestone_type
				value
			   	emails
			   	relations {
					id
					type
					name
			   	}
			   content
			}
			changable_statuses{
				key
				value
			}
			status_object {
				key
				value
			}
			parent {
				id
				name
			}
			from_type_id
		}
	`,
	Device: `
		fragment Device on DeviceType {
			id
			name
			code
			price
			current
			relations{
				id
				type
				display
			}
			can_update
			can_delete
			display_name
			warning_threshold
			alert_threshold
			is_configured
			images {
				id
				path
				file_name
				file_size
				extension
			}
			status
			used_at
			inventories(page:1, limit: -1) {
				data {
					id
					name
				}
			}
			manage_employees(page:1, limit: -1) {
				data {
					id
					name
					display_name
				}
			}
			kpis {
				...DeviceKpis
			}
			checked_times
			repaired_times
			repaired_total_cost
			category {
				id
				name
				properties
			}
			properties
			description
			per_item
			is_sharing
			incident {
				title
				task {
					id
					name
				}
				description
				image {
					path
				}
			}
			tasks (limit: 1, page: 1){
				data {
					...DeviceTask
				}
			}
			accessories {
				id
				name
				properties
				is_configured
				sensors {
					id
					name
					type
					unit
					current
					is_active
					limit_value
					warning_threshold
					alert_threshold
				}
			}
			total_hrs
			current_hrs
			maintain_hrs
			tags{
				id
				name
			}
		}
	`,
	DeviceDashboard: `
		fragment DeviceDashboard on DeviceType {
			id
			name
			code
			price
			current
			can_update
			can_delete
			display_name
			warning_threshold
			alert_threshold
			is_configured
			images {
				id
				path
				file_name
				file_size
				extension
			}
			status
			used_at
			manage_employees(page:1, limit: -1) {
				data {
					id
					name
					display_name
				}
			}
			checked_times
			repaired_times
			repaired_total_cost
			category {
				id
				name
				properties
			}
			properties
			per_item
			is_sharing
			accessories {
				id
				name
				properties
				is_configured
				sensors {
					id
					name
					type
					unit
					current
					is_active
					limit_value
					warning_threshold
					alert_threshold
				}
			}
		}
	`,
	DeviceTask: `
		fragment DeviceTask on CbsTask {
			id
			status
			employee {
				id
				name
			}
			details {
				id
				name
				status
				updated_at
				image{
					path
				}
				comment {
					title
				}
			}
		}
	`,
	DeviceStatus: `
		fragment DeviceStatus on CbsStatus {
			id
			code
			name
			color
		}
	`,
	DeviceKpis: `
		fragment DeviceKpis on CbsKpi {
			id
			name
			kpi_details {
				id
				name
			}
		}
	`,
	Category: `
		fragment Category on Category {
			id
			name
			can_update
			can_delete
			parent_id
			properties
			kpis {
				...Kpi
			}
			kpiv2s{
				id
				name
			}
		}
	`,
	Kpi: `
		fragment Kpi on CbsKpi {
			id
			name
			kpi_details {
				...KpiTaskDetail
			}
		}
  `,
	KpiTaskDetail: `
		fragment KpiTaskDetail on CbsTaskDetail {
			id
			name
			can_update
			can_delete
		}
	`,
	Accessory: `
		fragment Accessory on Accessory {
			id
			name
			code
			image {
				path
			}
			current
			can_update
			can_delete
			properties
			is_configured
			warning_threshold
			alert_threshold
			is_configured
			description
			type_id
			maintain_hrs
			current_hrs
			total_hrs
			device {
				id
				name
			}
			sensors {
				id
				name
				unit
				current
				is_active
				is_counter
				alert_threshold
				warning_threshold
			}
			files{
				id
				path
				file_name
				file_size
				extension
			}
			tags{
				id
				name
			}

		}
	`,
	TodoItemLogsByRelations: `
		fragment TodoItemLogsByRelations on Todolist {
			id
			name
			type
			code
			description
			forms {
				id
				name
				code
				properties
				display_name
				attachs {
					id
					path
					file_name
					file_size
					extension
				}
				controls {
					id
					title
					control
					children
					attributes
					is_optional
					description {
						value
						enable
						position
					}
				}
				is_submited
				can_update
				can_delete
			}
			properties

			speech {
				path
			}


		}
	`,
	TodoStatistic: `
		fragment TodoStatistic on TodolistStatisticType {
			details {
				id
				name
				display
			}
			department {
				id
				name
			}
		}
	`,
	TodoItemDetailAttachs: `
		fragment TodoItemDetailAttachs on Todolist {
			id
			name
			attachs{
				id
				path
				file_name
				file_size
				extension
			}

		}
	`,
	DeviceDetail: `
		fragment DeviceDetail on DeviceType {
			id
			name
			code
			price
			current
			relations{
				id
				type
				display
			}
			can_update
			can_delete
			display_name
			warning_threshold
			inspection_deadline
			alert_threshold
			is_configured
			images {
				id
				path
				file_name
				file_size
				extension
			}
			status
			used_at
			inventories(page:1, limit: -1) {
				data {
					id
					name
				}
			}
			manage_employees(page:1, limit: -1) {
				data {
					id
					name
					display_name
				}
			}
			kpis {
				...DeviceKpis
			}
			checked_times
			repaired_times
			repaired_total_cost
			category {
				id
				name
				properties
			}
			properties
			description
			per_item
			is_sharing
			incident {
				title
				task {
					id
					name
				}
				description
				image {
					path
				}
			}
			tasks (limit: 1, page: 1){
				data {
					...DeviceTask
				}
			}
			accessories {
				id
				name
				properties
				is_configured
				sensors {
					id
					name
					type
					unit
					current
					is_active
					limit_value
					warning_threshold
					alert_threshold
				}
			}
			total_hrs
			current_hrs
			maintain_hrs
			statistic_todolists{
				id
				name
				display
			}
			statistic_request{
				id
				name
				display
			}
			kpiv2s{
				id
				name
				maintain_hrs
				current_hrs
			}
			tags{
				id
				name
			}

		}
	`,
	Kpiv2: `
		fragment Kpiv2 on CbsKpiV2 {
			id
			name
			maintain_hrs
			current_hrs
			pivot_id
			kpiable_id
			kpiable_type
			can_update
			can_delete
			tags {
				id
				name
			}
		}
  `
};

export default createGraphqlClient("cmcbs");
